import { useState } from 'react';
import { formatDate } from '../../../lib/format';
import { STATION_LABEL } from '@dru/types';
import { Link, useParams } from 'react-router-dom';
import type { AuditRow, CatalogueTestDetail, CursorPage, Panel } from '@dru/types';
import { TEST_CATEGORY_LABEL, SAMPLE_TYPE_LABEL } from '@dru/types';
import {
  Alert, Button, Detail, DetailHeader, DetailSection, Field, InfoTip, Input, KeyFigures, SearchSelect, StatusBadge, Select, Textarea,
  TextSkeleton,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';
import { AnswerOptionsDialog, RangesDialog } from '../components/CatalogueDialogs';
import { LoincPicker, useLoincValue } from '../components/LoincPicker';
import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';
import { useAuth } from '../../../lib/auth';
import { CatalogueState, RetireAction, plural } from '../components/RetireAction';
import { ActivityTable, NotForYourRole } from './Audit';

interface DrawForm {
  sampleType: string;
  fastingHours: string;
  sexApplicable: string;
  ageMinYears: string;
  ageMaxYears: string;
  preparationNotes: string;
  plausibleLow: string;
  plausibleHigh: string;
}

/** A bound as typed: blank is "no bound", anything else must be a number. */
const boundOf = (raw: string): number | null | undefined => {
  const v = raw.trim();
  if (v === '') return null;
  return /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(v) ? Number(v) : undefined;
};

const drawForm = (t: CatalogueTestDetail): DrawForm => ({
  sampleType: t.sampleType && t.sampleType !== 'NONE' ? t.sampleType : 'BLOOD_SERUM',
  fastingHours: t.fastingHours == null ? '' : String(t.fastingHours),
  sexApplicable: t.sexApplicable ?? '',
  ageMinYears: t.ageMinYears == null ? '' : String(t.ageMinYears),
  ageMaxYears: t.ageMaxYears == null ? '' : String(t.ageMaxYears),
  preparationNotes: t.preparationNotes ?? '',
  plausibleLow: t.plausibleLow == null ? '' : String(t.plausibleLow),
  plausibleHigh: t.plausibleHigh == null ? '' : String(t.plausibleHigh),
});

const band = (r: { sex: string | null; ageMinYears: number | null; ageMaxYears: number | null }) =>
  `${r.sex ? r.sex.toLowerCase() : 'all genders'} · ${r.ageMinYears ?? 0}–${r.ageMaxYears ?? '∞'} yrs`;

const VALUE_TYPE_LABEL: Record<string, string> = {
  NUMERIC: 'Numeric',
  TEXT: 'Text / Qualitative',
  ENUM: 'Option Choice',
  BOOLEAN: 'Yes / No',
};

export function TestDetail() {
  if (!useAuth().permissions.has('test.read')) {
    return <NotForYourRole title="Test" what="The clinical catalogue is maintained by platform administrators." />;
  }
  return <TestDetailPage />;
}

function TestDetailPage() {
  const { id = '' } = useParams();
  const test = useQuery<CatalogueTestDetail>(() => api.get(`/tests/${id}`), [id]);
  const trail = useQuery<CursorPage<AuditRow>>(
    () => api.get(`/audit?entityType=TestDefinition&entityId=${id}&limit=50`),
    [id],
  );
  const [ranges, setRanges] = useState(false);
  const [answers, setAnswers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = test.data;
  useBreadcrumbTitle(t?.name);

  const [loinc, setLoinc] = useLoincValue(t?.loincCode ?? null, t?.loincLongName ?? null);
  const [savingLoinc, setSavingLoinc] = useState(false);
  const [consumer, setConsumer] = useState<string | null>(null);
  const [savingConsumer, setSavingConsumer] = useState(false);

  const [draw, setDraw] = useState<DrawForm | null>(null);
  const [savingDraw, setSavingDraw] = useState(false);

  async function saveDraw(form: DrawForm) {
    const low = boundOf(form.plausibleLow);
    const high = boundOf(form.plausibleHigh);
    if (low === undefined || high === undefined) {
      setError('The lowest and highest possible values must be plain numbers, or blank for no limit.');
      return;
    }
    setSavingDraw(true);
    setError(null);
    try {
      await api.patch(`/tests/${id}`, {
        ...(t?.valueType === 'NUMERIC' ? { plausibleLow: low, plausibleHigh: high } : {}),
        ...(t?.category === 'PATHOLOGY' ? { sampleType: form.sampleType } : {}),
        fastingHours: form.fastingHours.trim() ? Number(form.fastingHours) : null,
        sexApplicable: form.sexApplicable || null,
        ageMinYears: form.ageMinYears.trim() ? Number(form.ageMinYears) : null,
        ageMaxYears: form.ageMaxYears.trim() ? Number(form.ageMaxYears) : null,
        preparationNotes: form.preparationNotes.trim() || null,
      });
      setDraw(null);
      test.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSavingDraw(false);
    }
  }

  const panels = useQuery<Panel[]>(() => api.get('/panels'), []);
  const [desc, setDesc] = useState<string | null>(null);
  const [savingReport, setSavingReport] = useState(false);

  async function saveReportFields(patch: { panelCode?: string | null; description?: string | null }) {
    setSavingReport(true);
    setError(null);
    try {
      await api.patch(`/tests/${id}`, patch);
      setDesc(null);
      test.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSavingReport(false);
    }
  }

  async function saveConsumerName(value: string) {
    setSavingConsumer(true);
    setError(null);
    try {
      await api.patch(`/tests/${id}`, { consumerName: value.trim() || null });
      setConsumer(null);
      test.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSavingConsumer(false);
    }
  }

  async function saveLoinc(hit: { loincCode: string; longCommonName: string; ucumUnit: string | null } | null) {
    setLoinc(hit as never);
    setSavingLoinc(true);
    setError(null);
    try {
      await api.patch(
        `/tests/${id}`,
        hit
          ? {
              loincCode: hit.loincCode,
              loincLongName: hit.longCommonName,
              ...(hit.ucumUnit ? { ucumUnit: hit.ucumUnit } : {}),
            }
          : { loincCode: null, loincLongName: null, ucumUnit: null },
      );
      test.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSavingLoinc(false);
    }
  }

  // Throws, so the confirmation stays open with the reason when refused.
  async function toggle() {
    if (!t) return;
    await api.patch(`/tests/${id}`, { isActive: !t.isActive });
    test.reload();
  }

  const canManage = useAuth().permissions.has('test.manage');
  const livePackages = t?.packages.filter((p) => p.isActive) ?? [];
  const inDefaults = livePackages.filter((p) => p.defaultFor);

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Test"
        title={t?.name ?? (test.error ? 'Test not found' : 'Loading…')}
        badges={t && <CatalogueState active={t.isActive} />}
        subtitle={
          t &&
          [
            t.code,
            TEST_CATEGORY_LABEL[t.category] ?? t.category,
            t.station ? `captured at ${STATION_LABEL[t.station as never] ?? t.station}` : null,
            `${VALUE_TYPE_LABEL[t.valueType] ?? t.valueType}${t.unit ? ` (${t.unit})` : ''}`,
          ]
            .filter(Boolean)
            .join(' · ')
        }
        actions={
          t && canManage && (
            <>
              {t.valueType === 'NUMERIC' && (
                <Button variant="secondary" onClick={() => setRanges(true)}>
                  Edit reference bands
                </Button>
              )}
              {t.valueType === 'ENUM' && (
                <Button variant="secondary" onClick={() => setAnswers(true)}>
                  Edit allowed answers
                </Button>
              )}
              <RetireAction
                variant="button"
                active={t.isActive}
                name={t.name}
                what="test"
                onConfirm={toggle}
                impact={t.isActive
                  ? `In ${plural(livePackages.length, 'package')}; ${plural(t.resultCount, 'result')} recorded against it.`
                  : undefined}
                consequence={inDefaults.length
                  ? `Examinations raised from now on will not include it: ${inDefaults.map((p) => p.name).join(' and ')} stop running it for every company.`
                  : 'No default package runs it, so no employee’s examination changes.'}
              />
            </>
          )
        }
      />

      {t && (
        <KeyFigures
          figures={[
            { key: 'packages', label: 'In packages', value: livePackages.length, hint: inDefaults.length ? inDefaults.map((p) => p.defaultFor === 'PRE_EMPLOYMENT' ? 'Pre-employment' : 'Annual').join(' · ') : 'Not in a default package' },
            { key: 'results', label: 'Results recorded', value: t.resultCount },
            t.valueType === 'NUMERIC'
              ? { key: 'bands', label: 'Reference bands', value: t.referenceRanges.length, hint: t.referenceRanges.length ? undefined : 'never flagged', tone: t.referenceRanges.length ? undefined : 'dueSoon' as const }
              : t.valueType === 'ENUM'
                ? { key: 'answers', label: 'Allowed answers', value: t.answerOptions.length, hint: t.answerOptions.length ? undefined : 'cannot be entered', tone: t.answerOptions.length ? undefined : 'overdue' as const }
                : null,
          ]}
        />
      )}

      {test.error && <Alert tone="danger">{test.error}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      {test.loading && !t && <TextSkeleton lines={6} />}

      {t && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
          {/* 1. Clinical Definition */}
          <DetailSection title="Clinical definition">
            <div className="grid gap-3 sm:grid-cols-2">
              <Detail label="Code" value={t.code} mono />
              <Detail label="Name" value={t.name} />
              <Detail label="Category" value={TEST_CATEGORY_LABEL[t.category] ?? t.category} />
              <Detail
                label="Captured at"
                value={
                  t.station ? (
                    STATION_LABEL[t.station as never] ?? t.station
                  ) : (
                    <span className="text-neutral-500">Not required</span>
                  )
                }
              />
              <Detail label="Result" value={VALUE_TYPE_LABEL[t.valueType] ?? t.valueType} />
              <Detail label="Unit" value={t.unit || <span className="text-neutral-500">None</span>} />
              <Detail label="Added" value={formatDate(t.createdAt)} />
              <Detail label="Last changed" value={formatDate(t.updatedAt)} />
            </div>
          </DetailSection>

          {/* 2. Universal Clinical Code (LOINC) */}
          <DetailSection
            title="LOINC mapping"
            phoneFolded
            action={(
              <InfoTip label="About LOINC mapping">
                The international code analysers, partner labs and health records use for this test.
                Changing it does not change what employees are tested for.
              </InfoTip>
            )}
          >
            <LoincPicker value={loinc} onChange={saveLoinc} disabled={savingLoinc || !canManage} label="LOINC term" className="" />
            {t.ucumUnit && t.ucumUnit !== t.unit && (
              <p className="mt-2 text-caption text-neutral-500">
                Display unit: <span className="font-mono">{t.unit}</span> · Interface unit:{' '}
                <span className="font-mono">{t.ucumUnit}</span>
              </p>
            )}
          </DetailSection>

          {/* 3. What a worker reads */}
          <DetailSection
            title="What an employee reads"
            action={(
              <InfoTip label="About what an employee reads">
                How this test is named and explained on an employee&rsquo;s health report and in the employee app.
              </InfoTip>
            )}
          >
            <div className="space-y-4">
              <Field
                label="Name on reports"
                status={t.consumerName
                  ? `Custom name in use. Clear it to fall back to “${t.name}”.`
                  : `Blank: reports use the catalogue name, “${t.name}”.`}
              >
                {(p) => (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      {...p}
                      value={consumer ?? t.consumerName ?? ''}
                      onChange={(e) => setConsumer(e.target.value)}
                      placeholder={t.name}
                      disabled={savingConsumer || !canManage}
                      className="min-w-0 flex-[1_1_12rem]"
                    />
                    {canManage && (
                      <Button
                        variant="secondary"
                        loading={savingConsumer}
                        disabled={consumer === null || consumer === (t.consumerName ?? '')}
                        onClick={() => saveConsumerName(consumer ?? '')}
                      >
                        Save name
                      </Button>
                    )}
                  </div>
                )}
              </Field>

              <SearchSelect
                label="Report section"
                hint="Saved as soon as it is chosen."
                info="Where on the health report it is grouped."
                value={t.panelCode ?? ''}
                disabled={savingReport || !canManage}
                onChange={(v) => saveReportFields({ panelCode: v || null })}
                placeholder={`Default (${TEST_CATEGORY_LABEL[t.category] ?? t.category})`}
                searchPlaceholder="Search report sections…"
                options={[
                  { value: '', label: `Default (${TEST_CATEGORY_LABEL[t.category] ?? t.category})` },
                  ...(panels.data ?? []).map((pn) => ({ value: pn.code, label: pn.name, hint: pn.bodySystem })),
                ]}
              />

              <Field label="Plain-language description" hint="What this test measures, in words an employee understands.">
                {(p) => (
                  <Textarea
                    {...p}
                    value={desc ?? t.description ?? ''}
                    onChange={(e) => setDesc(e.target.value)}
                    disabled={savingReport || !canManage}
                    rows={3}
                    placeholder="Measures the oxygen-carrying protein in your blood."
                  />
                )}
              </Field>
              {canManage && (
                <div className="flex justify-end">
                  <Button
                    variant="secondary"
                    loading={savingReport}
                    disabled={desc === null || desc === (t.description ?? '')}
                    onClick={() => saveReportFields({ description: (desc ?? '').trim() || null })}
                  >
                    Save description
                  </Button>
                </div>
              )}
            </div>
          </DetailSection>

          {/* 4. Sample and eligibility */}
          <DetailSection
            title="Sample and eligibility"
            action={(
              <InfoTip label="About sample and eligibility">
                Who this test applies to and how the employee prepares. An employee outside the
                sex or age range is not asked for it, even where a protocol lists it.
              </InfoTip>
            )}
          >
            {(() => {
              const f = draw ?? drawForm(t);
              const set = (k: keyof DrawForm) => (v: string) => setDraw({ ...f, [k]: v });
              const dirty = draw !== null && JSON.stringify(draw) !== JSON.stringify(drawForm(t));
              const locked = savingDraw || !canManage;
              const digits = (v: string) => v.replace(/[^0-9]/g, '');
              return (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {t.category === 'PATHOLOGY' ? (
                      <Field label="Sample tube">
                        {(p) => (
                          <Select {...p} value={f.sampleType} onChange={(e) => set('sampleType')(e.target.value)} disabled={locked}>
                            {['BLOOD_EDTA', 'BLOOD_SERUM', 'BLOOD_FLUORIDE', 'URINE', 'SPUTUM'].map((v) => (
                              <option key={v} value={v}>{SAMPLE_TYPE_LABEL[v]}</option>
                            ))}
                          </Select>
                        )}
                      </Field>
                    ) : (
                      <Detail label="Sample" value="None — measured at a station" />
                    )}
                    {t.valueType === 'NUMERIC' && (
                      <>
                        <Field label={`Lowest possible value${t.unit ? ` (${t.unit})` : ''}`}
                          hint="Not the normal range: the lowest this test can physically read."
                          info="Entry below it is refused as a typo. Blank for no limit.">
                          {(p) => (
                            <Input {...p} value={f.plausibleLow} inputMode="decimal" placeholder="No limit" disabled={locked}
                              onChange={(e) => set('plausibleLow')(e.target.value)} />
                          )}
                        </Field>
                        <Field label={`Highest possible value${t.unit ? ` (${t.unit})` : ''}`}
                          hint="Entry above this is refused."
                          info="A haemoglobin of 132 is a missed decimal point, not a finding.">
                          {(p) => (
                            <Input {...p} value={f.plausibleHigh} inputMode="decimal" placeholder="No limit" disabled={locked}
                              onChange={(e) => set('plausibleHigh')(e.target.value)} />
                          )}
                        </Field>
                      </>
                    )}
                    <Field label="Fasting (hours)" hint="Blank when none is needed.">
                      {(p) => (
                        <Input {...p} value={f.fastingHours} inputMode="numeric" placeholder="None" disabled={locked}
                          onChange={(e) => set('fastingHours')(digits(e.target.value))} />
                      )}
                    </Field>
                    <Field label="Applies to">
                      {(p) => (
                        <Select {...p} value={f.sexApplicable} onChange={(e) => set('sexApplicable')(e.target.value)} disabled={locked}>
                          <option value="">All employees</option>
                          <option value="MALE">Male employees only</option>
                          <option value="FEMALE">Female employees only</option>
                        </Select>
                      )}
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="From age">
                        {(p) => (
                          <Input {...p} value={f.ageMinYears} inputMode="numeric" placeholder="No limit" disabled={locked}
                            onChange={(e) => set('ageMinYears')(digits(e.target.value))} />
                        )}
                      </Field>
                      <Field label="To age">
                        {(p) => (
                          <Input {...p} value={f.ageMaxYears} inputMode="numeric" placeholder="No limit" disabled={locked}
                            onChange={(e) => set('ageMaxYears')(digits(e.target.value))} />
                        )}
                      </Field>
                    </div>
                  </div>
                  <Field label="Preparation for the employee" hint="Printed on the appointment card.">
                    {(p) => (
                      <Textarea {...p} value={f.preparationNotes} rows={2} disabled={locked}
                        placeholder="Fast for 10–12 hours before the test. Bring spectacles if worn."
                        onChange={(e) => set('preparationNotes')(e.target.value)} />
                    )}
                  </Field>
                  {canManage && (
                    <div className="flex justify-end gap-2">
                      {dirty && (
                        <Button variant="ghost" onClick={() => setDraw(null)} disabled={savingDraw}>
                          Discard changes
                        </Button>
                      )}
                      <Button variant="secondary" loading={savingDraw} disabled={!dirty} onClick={() => saveDraw(f)}>
                        Save sample and eligibility
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}
          </DetailSection>

          {/* 5. Allowed Options (ENUM only) */}
          {t.valueType === 'ENUM' && (
            <DetailSection title={`Allowed answers (${t.answerOptions.length})`}>
              {t.answerOptions.length === 0 ? (
                <Alert tone="danger" title="Nothing can be entered against this test">
                  A technician chooses from these answers, and there are none yet. Add them with
                  &ldquo;Edit allowed answers&rdquo; above.
                </Alert>
              ) : (
                <ul className="divide-y divide-neutral-100 text-small">
                  {t.answerOptions.map((o) => (
                    <li key={o.code} className="flex items-baseline justify-between gap-2 py-1.5">
                      <span className="text-neutral-800">
                        {o.display} <span className="font-mono text-caption text-neutral-500">{o.code}</span>
                      </span>
                      {o.isAbnormal && <StatusBadge tone="overdue">Flagged abnormal</StatusBadge>}
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>
          )}

          {/* 6. Reference Bands (NUMERIC only) */}
          {t.valueType === 'NUMERIC' && (
            <DetailSection title={`Reference bands (${t.referenceRanges.length})`}>
              {t.referenceRanges.length === 0 ? (
                <Alert tone="warning" title="Never flagged">
                  With no band, a result is recorded but never marked high, low or critical.
                </Alert>
              ) : (
                <ul className="divide-y divide-neutral-100 text-small">
                  {t.referenceRanges.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                      <span className="text-neutral-800">
                        {band(r)}
                        {r.label ? ` · ${r.label}` : ''}
                      </span>
                      <span className="tabular-nums text-neutral-600">
                        {r.low ?? '—'} – {r.high ?? '—'}
                        {(r.criticalLow != null || r.criticalHigh != null) &&
                          ` · critical ${r.criticalLow ?? '—'} / ${r.criticalHigh ?? '—'}`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>
          )}

          {/* 8. In Packages */}
          <DetailSection phoneFolded title={`Packages that contain it (${livePackages.length})`}>
            {t.packages.length === 0 ? (
              <p className="text-small text-neutral-500">Not in any package.</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {/* Live first — the defaults lead — then the retired catalogue. */}
                {[...t.packages].sort((a, b) => Number(b.isActive) - Number(a.isActive) || Number(!!b.defaultFor) - Number(!!a.defaultFor)).map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 py-1.5 text-small">
                    <Link to={`/admin/masters/packages/${p.id}`} className="inline-block py-1 text-primary-700 hover:underline">
                      {p.name} <span className="font-mono text-caption text-neutral-500">{p.code} v{p.version}</span>
                    </Link>
                    {p.defaultFor ? <StatusBadge tone="covered">Default</StatusBadge> : !p.isActive && <StatusBadge>Retired</StatusBadge>}
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>

          {/* 9. History */}
          <DetailSection title="History" className="lg:col-span-2">
            <ActivityTable
              rows={trail.data?.items ?? []}
              loading={trail.loading}
              error={trail.error}
              onRetry={trail.reload}
              emptyHint="Changes to this test appear here."
            />
          </DetailSection>
        </div>
      )}

      {t && ranges && (
        <RangesDialog
          test={{ ...t, usedByProtocols: t.protocols.length, resultCount: t.resultCount }}
          onClose={() => setRanges(false)}
          onDone={() => {
            setRanges(false);
            test.reload();
          }}
        />
      )}
      {t && answers && (
        <AnswerOptionsDialog
          test={t}
          onClose={() => setAnswers(false)}
          onDone={() => {
            setAnswers(false);
            test.reload();
          }}
        />
      )}
    </div>
  );
}
